import {
  Autocomplete,
  InputLabel,
  Stack,
  TextField,
  TextFieldProps,
  ListItemText,
} from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useLazySearchAddressQuery } from 'store/address/address.api'
import { AddressDto } from 'store/address/address.types'
import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useBlockBrowserAutofill } from '../../../hooks/use-block-browser-autofill'

type AutocompleteAddressFieldProps = {
  label: string
  name: string
  placeholder?: string
  addressLine1Name?: string
  addressLine2Name?: string
  highlight?: boolean
  bold?: boolean
} & Omit<TextFieldProps, 'name' | 'label' | 'placeholder'>

const AutocompleteAddressField = ({
  label,
  name,
  placeholder = '',
  addressLine1Name = 'addressLine1',
  addressLine2Name = 'addressLine2',
  highlight,
  bold,
  ...rest
}: AutocompleteAddressFieldProps) => {
  const { control, watch, setValue } = useFormContext()
  const { blockBrowserAutofill, domFieldName, domFieldId, htmlInputProps } =
    useBlockBrowserAutofill(name)
  const [searchAddress, { data: addresses, isLoading }] = useLazySearchAddressQuery()
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Sort addresses to prioritize Canadian addresses
  const sortedAddresses = useMemo(() => {
    if (!addresses) return []
    const canadianAddresses = addresses.filter(
      (addr) => addr.Country?.toLowerCase() === 'canada' || addr.Country?.toLowerCase() === 'ca'
    )
    const otherAddresses = addresses.filter(
      (addr) => addr.Country?.toLowerCase() !== 'canada' && addr.Country?.toLowerCase() !== 'ca'
    )
    return [...canadianAddresses, ...otherAddresses]
  }, [addresses])

  // Watch both address fields to combine them for search
  const addressLine1 = watch(addressLine1Name)
  const addressLine2 = watch(addressLine2Name)

  // Debounced search function
  const debouncedSearch = useCallback(
    (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        if (query.trim().length > 0) {
          searchAddress({ addressQuery: query })
        }
      }, 300)
    },
    [searchAddress]
  )

  // Combine address lines for search query
  const getSearchQuery = useCallback(
    (currentInput: string) => {
      const parts: string[] = []
      
      if (name === addressLine1Name) {
        // User is typing in addressLine1 - use current input
        if (currentInput.trim()) {
          parts.push(currentInput)
        }
        // Also include addressLine2 if it exists
        if (addressLine2) {
          parts.push(addressLine2)
        }
      } else if (name === addressLine2Name) {
        // User is typing in addressLine2 - combine with addressLine1
        if (addressLine1) {
          parts.push(addressLine1)
        }
        if (currentInput.trim()) {
          parts.push(currentInput)
        }
      }
      
      return parts.join(' ').trim()
    },
    [name, addressLine1Name, addressLine2Name, addressLine1, addressLine2]
  )

  // Handle input change
  const handleInputChange = useCallback(
    (event: any, newInputValue: string, reason?: string) => {
      // When reason is 'reset', it means user selected an option
      // In this case, handleSelection will set the correct street address value
      // So we should NOT update inputValue here to prevent the description from showing
      if (reason === 'reset') {
        return
      }
      
      setInputValue(newInputValue)
      const query = getSearchQuery(newInputValue)
      if (query.length > 0) {
        debouncedSearch(query)
        setOpen(true)
      } else {
        setOpen(false)
      }
    },
    [getSearchQuery, debouncedSearch]
  )

  // Handle selection - this will be called from the onChange handler
  const handleSelection = useCallback(
    (newValue: AddressDto | null, onChange: (value: string) => void) => {
      if (newValue) {
        // Build street address for Address Line 1 (street number + street name)
        const streetParts: string[] = []
        if (newValue.streetNumber) {
          streetParts.push(newValue.streetNumber)
        }
        if (newValue.streetName) {
          streetParts.push(newValue.streetName)
        }
        const streetAddress = streetParts.join(' ').trim()

        // Determine the value to set for the current field
        // Only handle address line 1 (address line 2 is now a regular text field)
        if (name === addressLine1Name) {
          // Address Line 1 gets: ONLY street number + street name (NEVER the full description)
          // ALWAYS use streetNumber + streetName when available (which should be most cases)
          let fieldValue = streetAddress
          
          // Fallback: If streetAddress is empty (no streetNumber or streetName), 
          // extract just the street part from description (before first comma)
          if (!fieldValue && newValue.description) {
            // Description format: "street, city, province postal, country"
            // Extract only the part before the first comma (the street address)
            const parts = newValue.description.split(',')
            if (parts.length > 0) {
              fieldValue = parts[0].trim()
            }
          }
          
          // CRITICAL: fieldValue should NEVER be the full description
          // It must only be: streetNumber + streetName (e.g., "121 Healey Road")
          // or the first part before comma if street components are missing
          
          // Set address line 1 with just the street address
          setValue(addressLine1Name, fieldValue)
          
          // Address Line 2 gets: unit number if available
          if (newValue.unitNumber) {
            setValue(addressLine2Name, newValue.unitNumber)
          } else {
            // Clear address line 2 if no unit number
            setValue(addressLine2Name, '')
          }
          
          // Update the form field through Controller's onChange with street address only
          onChange(fieldValue)
          
          // CRITICAL: Update input value to show ONLY the street address (NOT the description)
          // This ensures the input field displays "121 Healey Road" not "121 Healey Rd, Bolton, ON L7E 5B2, Canada"
          setInputValue(fieldValue)
        } else {
          // This shouldn't happen since address line 2 is now a regular field
          onChange('')
        }

        // Set other fields if they exist (these go to separate fields, NOT in address line 1)
        if (newValue.city) {
          setValue('city', newValue.city)
        }
        if (newValue.state) {
          setValue('province', newValue.state)
        }
        if (newValue.zipCode) {
          setValue('postalCode', newValue.zipCode)
        }
      } else {
        onChange('')
        setInputValue('')
      }
      setOpen(false)
    },
    [name, addressLine1Name, addressLine2Name, setValue]
  )

  // Sync inputValue with form value on mount
  useEffect(() => {
    const currentValue = watch(name)
    if (currentValue) {
      setInputValue(currentValue)
    }
  }, [name, watch])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return (
    <Stack spacing={1}>
      <InputLabel
        sx={{
          color: highlight ? 'error.main' : 'secondary.dark',
          fontWeight: highlight || bold ? 700 : 300,
        }}
      >
        {label}
      </InputLabel>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            open={open && (sortedAddresses?.length || 0) > 0}
            onOpen={() => {
              if (inputValue.trim().length > 0) {
                const query = getSearchQuery(inputValue)
                if (query.length > 0) {
                  debouncedSearch(query)
                  setOpen(true)
                }
              }
            }}
            onClose={() => setOpen(false)}
            options={sortedAddresses}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.description || ''
            }
            renderOption={(props, option) => (
              <li {...props} key={option.description}>
                <ListItemText
                  primary={option.description}
                  primaryTypographyProps={{
                    fontSize: '0.875rem', // Small text size (14px)
                  }}
                />
              </li>
            )}
            loading={isLoading}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={(event, newValue) => {
              // Handle string values from freeSolo mode
              if (typeof newValue === 'string') {
                onChange(newValue)
                setInputValue(newValue)
                setOpen(false)
                return
              }
              // Handle AddressDto or null
              handleSelection(newValue, onChange)
            }}
            value={
              value && sortedAddresses
                ? sortedAddresses.find((addr) => {
                    if (name === addressLine1Name) {
                      // Match based on street address (streetNumber + streetName)
                      const streetAddr = addr.streetNumber && addr.streetName
                        ? `${addr.streetNumber} ${addr.streetName}`.trim()
                        : null
                      // Also check if value matches the first part of description (before comma)
                      const descStreetPart = addr.description ? addr.description.split(',')[0].trim() : null
                      return streetAddr === value || descStreetPart === value
                    }
                    return addr.description === value
                  }) || null
                : null
            }
            isOptionEqualToValue={(option, val) => {
              if (!val) return false
              if (typeof val === 'string') {
                if (name === addressLine1Name) {
                  // Match based on street address components
                  const streetAddr = option.streetNumber && option.streetName
                    ? `${option.streetNumber} ${option.streetName}`.trim()
                    : null
                  // Also check first part of description
                  const descStreetPart = option.description ? option.description.split(',')[0].trim() : null
                  return streetAddr === val || descStreetPart === val
                }
                return option.description === val
              }
              return option.description === val.description
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...rest}
                placeholder={placeholder}
                size="small"
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  ...htmlInputProps,
                  name: domFieldName,
                  id: domFieldId,
                  autoComplete: blockBrowserAutofill ? 'new-password' : 'off',
                }}
              />
            )}
            freeSolo
            filterOptions={(x) => x}
          />
        )}
      />
    </Stack>
  )
}

export default AutocompleteAddressField
